import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DOCUMENT_FORMATS, FileTypeGroup, MyFileSource } from '@apm/contracts';
import {
  type MyFilesSearchState,
  MyFilesPage,
} from '../../features/my-files/components/my-files-page';
import { MY_FILES_VIEWS } from '../../features/my-files/view-mode';

/**
 * Thư mục, kiểu xem, ô tìm, bộ lọc và thứ tự nằm trên URL — gửi link "ảnh trong
 * Tệp của tôi, dạng lưới" cho người khác vẫn mở đúng cảnh.
 *
 * Mọi trường `.catch(undefined)`: một link hỏng rơi về mặc định thay vì làm
 * trắng cả trang. Các enum là chuỗi `JSON.parse` không đọc nổi nên không dính
 * bẫy APM-903b; riêng `q` là chữ tự do — `?q=2026` tới đây dưới dạng **số**,
 * nên đổi về chuỗi trước khi kiểm, không thì ô tìm biến mất im lặng.
 */
export const MyFilesSearch = z.object({
  folder: MyFileSource.optional().catch(undefined),
  /**
   * Thư mục tự tạo đang đứng (APM-MYF-j) — chỉ có nghĩa khi `folder = 'mine'`.
   * uuid là chuỗi mà `JSON.parse` không đọc nổi, nên tới đây vẫn là chuỗi.
   */
  fid: z.uuid().optional().catch(undefined),
  /**
   * Thư mục ảo "Bản nháp" (APM-MYF-l). `z.boolean()`: router `JSON.parse` giá trị
   * nên `?drafts=true` tới đây là boolean — schema chuỗi sẽ trượt (bẫy APM-903b).
   */
  drafts: z.boolean().optional().catch(undefined),
  /** Thư mục ảo "Được chia sẻ với tôi" (APM-SHR-d) — boolean như `drafts`. */
  shared: z.boolean().optional().catch(undefined),
  view: z.enum(MY_FILES_VIEWS).optional().catch(undefined),
  q: z
    .preprocess(
      (value) => (typeof value === 'number' || typeof value === 'boolean' ? String(value) : value),
      z.string().trim().min(1).max(200).optional(),
    )
    .catch(undefined),
  type: FileTypeGroup.optional().catch(undefined),
  sort: z.enum(['createdAt', 'name', 'size']).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  /**
   * Hộp soạn đang mở (APM-MYF-m, `editor-route.ts`): tài liệu mới chưa lưu gì,
   * bản nháp, hay tệp có tên. uuid/enum là chuỗi `JSON.parse` không đọc nổi.
   */
  new: z.enum(DOCUMENT_FORMATS).optional().catch(undefined),
  draft: z.uuid().optional().catch(undefined),
  edit: z.uuid().optional().catch(undefined),
  /**
   * Tệp đang mở trong khung xem trước (APM-PVLINK) — chép gửi người khác là họ mở đúng khung ấy.
   * uuid: `JSON.parse` không đọc nổi nên tới đây vẫn là chuỗi.
   */
  preview: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/files')({
  validateSearch: MyFilesSearch,
  component: FilesPage,
});

/**
 * Trang "Tệp" — trình quản lý file của mỗi người (APM-MYF-c,
 * docs/plan-my-files.md §4.1). Mọi người đăng nhập đều vào được: danh sách chỉ
 * có file **chính người xem** đã tải lên, phạm vi do API quyết (luật #5).
 */
function FilesPage(): ReactNode {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const onSearchChange = useCallback(
    (patch: Partial<MyFilesSearchState>, options?: { readonly push?: boolean }) => {
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: !options?.push });
    },
    [navigate],
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-medium">{t('myFiles.title')}</h1>
      </header>

      <MyFilesPage search={search} onSearchChange={onSearchChange} />
    </div>
  );
}
